import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';
import { useOutletContext, useNavigate } from 'react-router-dom';
import AvatarWithStatus from '../AvatarWithStatus';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { acme, acmeInfoCalloutSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  Card,
  CardContent,
  Input,
  Button,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
} from '../ui';

const profileFieldRowSx = {
  display: 'flex',
  alignItems: { xs: 'stretch', sm: 'flex-end' },
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 1.5,
};

const ProfileFieldRow = ({ label, value, onChange, disabled, required }) => (
  <Box sx={profileFieldRowSx}>
    <Input
      label={label}
      value={value ?? ''}
      required={required}
      InputProps={{ readOnly: true }}
      sx={{ flex: 1 }}
    />
    <Button
      variant="outline"
      onClick={onChange}
      disabled={disabled}
      sx={{ flexShrink: 0, minWidth: { sm: 100 } }}
    >
      Change
    </Button>
  </Box>
);

const MyProfile = () => {
  const { updateUser } = useOutletContext();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogField, setDialogField] = useState(null);
  const [dialogValue, setDialogValue] = useState('');

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [deleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const [subscriptionWarningOpen, setSubscriptionWarningOpen] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/api/users/me');
        setProfileData(res.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <PageShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: acme.colors.brand }} />
        </Box>
      </PageShell>
    );
  }

  if (!profileData) {
    return (
      <PageShell>
        <Typography sx={{ textAlign: 'center', color: acme.colors.mutedForegroundSolid }}>
          Profile not found.
        </Typography>
      </PageShell>
    );
  }

  const handleOpenDialog = (fieldKey) => {
    setDialogField(fieldKey);
    setDialogValue(profileData[fieldKey] || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogField(null);
    setDialogValue('');
  };

  const handleSaveDialog = async () => {
    if (!dialogField) return;
    try {
      const res = await axios.put('/api/users/me', {
        [dialogField]: dialogValue,
      });
      setProfileData((prev) => ({ ...prev, [dialogField]: dialogValue }));
      updateUser(res.data);
      handleCloseDialog();
      setSuccessMessage(`${formatDialogField(dialogField)} Success!`);
    } catch (error) {
      console.error('Update error:', error);
      setErrorMessage('Update error');
    }
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match!');
      return;
    }
    try {
      const res = await axios.put('/api/users/me/password', { newPassword });
      setSuccessMessage('Password updated successfully!');
      handleClosePasswordDialog();
      if (res.data && res.data.user) {
        updateUser(res.data.user);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessage('Error changing password.');
    }
  };

  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarFile(e.target.files[0]);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const res = await axios.put('/api/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data && res.data.user) {
        setProfileData((prev) => ({
          ...prev,
          avatarUrl: res.data.user.avatarUrl,
        }));
        updateUser({ avatarUrl: res.data.user.avatarUrl });
      }
      setSuccessMessage('Avatar updated successfully!');
      setAvatarFile(null);
    } catch (error) {
      console.error('Avatar upload error:', error);
      setErrorMessage('Avatar update error.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const formatDialogField = (field) => {
    if (!field) return '';
    const withSpaces = field.replace(/([A-Z])/g, ' $1');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const isUserRestricted = () =>
    profileData?.status === 'Moderation' || profileData?.status === 'Block';

  const handleOpenDeleteAccountDialog = () => {
    setDeleteAccountDialogOpen(true);
    setDeleteConfirmText('');
  };

  const handleCloseDeleteAccountDialog = () => {
    setDeleteAccountDialogOpen(false);
    setDeleteConfirmText('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== profileData.login) {
      setErrorMessage('Please enter your login correctly to confirm account deletion');
      return;
    }

    try {
      await axios.delete('/api/users/me');

      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      updateUser(null);

      setDeleteAccountDialogOpen(false);
      setDeleteSuccessModalOpen(true);

      let count = 3;
      const timer = setInterval(() => {
        count -= 1;
        setRedirectCountdown(count);

        if (count <= 0) {
          clearInterval(timer);
          window.location.href = '/';
        }
      }, 1000);
    } catch (error) {
      console.error('Error deleting account:', error);

      if (error.response?.data?.error === 'ACTIVE_SUBSCRIPTION') {
        setSubscriptionInfo({
          message: error.response.data.message,
          billingUrl: error.response.data.billingUrl,
          subscriptionStatus: error.response.data.subscriptionStatus,
        });
        setDeleteAccountDialogOpen(false);
        setSubscriptionWarningOpen(true);
      } else {
        setErrorMessage('Failed to delete account. Please try again.');
      }
    }
  };

  if (subscriptionWarningOpen) {
    return (
      <Dialog open={subscriptionWarningOpen} onClose={() => setSubscriptionWarningOpen(false)} maxWidth="sm" fullWidth>
        <DialogBody sx={{ textAlign: 'center', py: 4 }}>
          <WarningIcon sx={{ fontSize: 56, color: acme.colors.warning, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
            Active Subscription Detected
          </Typography>
          <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 3 }}>
            {subscriptionInfo?.message || 'You cannot delete your account while you have an active subscription.'}
          </Typography>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            To delete your account, please first cancel your subscription:
          </Typography>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 3, textAlign: 'left' }}>
            1. Go to the <strong>Billing</strong> page<br />
            2. Find the <strong>Subscription Management</strong> section<br />
            3. Click <strong>Cancel Subscription</strong><br />
            4. Wait for the subscription to end, then try deleting your account again
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => {
                setSubscriptionWarningOpen(false);
                navigate('/billing');
              }}
            >
              Go to Billing Page
            </Button>
            <Button variant="outline" onClick={() => setSubscriptionWarningOpen(false)}>
              Close
            </Button>
          </Box>
        </DialogBody>
      </Dialog>
    );
  }

  if (deleteSuccessModalOpen) {
    return (
      <Dialog open={deleteSuccessModalOpen} maxWidth="xs" fullWidth>
        <DialogBody sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: acme.colors.brand, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
            Account Deleted Successfully
          </Typography>
          <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
            Your account has been permanently deleted. Thank you for using our services.
          </Typography>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
            Redirecting to homepage in {redirectCountdown} seconds...
          </Typography>
        </DialogBody>
      </Dialog>
    );
  }

  const restricted = isUserRestricted();

  return (
    <PageShell>
      <PageHeader title="My Profile" subtitle="Manage your account details and security settings." sx={{ mb: 2 }} />
      <PageSnackbars {...snackbarProps} />

      <Card sx={{ maxWidth: 720, mx: 'auto' }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Box sx={{ position: 'relative', mb: avatarFile ? 2 : 0 }}>
              <AvatarWithStatus
                src={
                  avatarFile
                    ? URL.createObjectURL(avatarFile)
                    : profileData.avatarUrl
                }
                alt={profileData.login}
                user={profileData}
                sx={{ width: 120, height: 120 }}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  right: -8,
                  backgroundColor: acme.colors.background,
                  border: `1px solid ${acme.colors.border}`,
                  boxShadow: acme.shadow.sm,
                  p: 0.75,
                  '&:hover': { backgroundColor: acme.colors.muted },
                }}
                size="small"
                component="label"
                disabled={restricted}
              >
                <EditIcon fontSize="small" />
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={restricted}
                />
              </IconButton>
            </Box>

            {avatarFile && (
              <Button
                onClick={handleUploadAvatar}
                disabled={uploadingAvatar || restricted}
                startIcon={uploadingAvatar ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {uploadingAvatar ? 'Uploading…' : 'Save new avatar'}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'grid', rowGap: 2.5 }}>
            <ProfileFieldRow
              label="Login"
              value={profileData.login}
              required
              disabled={restricted}
              onChange={() => handleOpenDialog('login')}
            />
            <ProfileFieldRow
              label="Email address"
              value={profileData.email}
              required
              disabled={restricted}
              onChange={() => handleOpenDialog('email')}
            />
            <ProfileFieldRow
              label="Password"
              value="******"
              required
              disabled={restricted}
              onChange={handleOpenPasswordDialog}
            />
            <ProfileFieldRow
              label="First name"
              value={profileData.firstName}
              disabled={restricted}
              onChange={() => handleOpenDialog('firstName')}
            />
            <ProfileFieldRow
              label="Last name"
              value={profileData.lastName}
              disabled={restricted}
              onChange={() => handleOpenDialog('lastName')}
            />
            <ProfileFieldRow
              label="Phone number"
              value={profileData.phone}
              disabled={restricted}
              onChange={() => handleOpenDialog('phone')}
            />
            <ProfileFieldRow
              label="Company name"
              value={profileData.company}
              disabled={restricted}
              onChange={() => handleOpenDialog('company')}
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, color: acme.colors.destructive, mb: 2 }}>
            Danger Zone
          </Typography>

          {restricted && (
            <Box
              sx={{
                ...acmeInfoCalloutSx,
                mb: 3,
                borderColor: profileData?.status === 'Block' ? acme.colors.destructive : acme.colors.warning,
                backgroundColor:
                  profileData?.status === 'Block'
                    ? 'rgba(239, 68, 68, 0.06)'
                    : 'rgba(245, 158, 11, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <WarningIcon
                  sx={{
                    color: profileData?.status === 'Block' ? acme.colors.destructive : acme.colors.warning,
                    mt: 0.25,
                    fontSize: 20,
                  }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                    {profileData?.status === 'Block' ? 'Account Blocked' : 'Account Under Moderation'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                    {profileData?.status === 'Block'
                      ? 'Your account has been blocked. You cannot modify your profile or delete your account. Please contact support for assistance.'
                      : 'Your account is under moderation. Profile modifications and account deletion are temporarily disabled. Please wait for moderation to complete or contact support.'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Delete Account</Typography>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Once deleted, your account cannot be recovered.
              </Typography>
            </Box>
            <Button
              variant="destructive"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteAccountDialog}
              disabled={restricted}
              sx={{ flexShrink: 0 }}
            >
              Delete Account
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitleText>Change {formatDialogField(dialogField)}</DialogTitleText>
        <DialogBody>
          <Input
            value={dialogValue}
            onChange={(e) => setDialogValue(e.target.value)}
            autoFocus
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button onClick={handleSaveDialog}>Save</Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitleText>Change Password</DialogTitleText>
        <DialogBody>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Box>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleClosePasswordDialog}>
            Cancel
          </Button>
          <Button onClick={handleChangePassword}>Save</Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={deleteAccountDialogOpen} onClose={handleCloseDeleteAccountDialog} maxWidth="sm" fullWidth>
        <DialogTitleText sx={{ color: acme.colors.destructive }}>
          Delete Your Account
        </DialogTitleText>
        <DialogBody>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This action cannot be undone.
          </Typography>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
            All your data will be permanently deleted, including:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 2.5, color: acme.colors.mutedForegroundSolid }}>
            <li>Profile information</li>
            <li>Email campaigns</li>
            <li>Contact lists</li>
            <li>All other data associated with your account</li>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            To confirm, please type your login: <strong>{profileData?.login}</strong>
          </Typography>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Enter your login"
            error={deleteConfirmText !== '' && deleteConfirmText !== profileData?.login}
            helperText={
              deleteConfirmText !== '' && deleteConfirmText !== profileData?.login
                ? "Login doesn't match"
                : ''
            }
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDeleteAccountDialog}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== profileData?.login}
          >
            Delete My Account
          </Button>
        </DialogFooter>
      </Dialog>
    </PageShell>
  );
};

export default MyProfile;
